import { Box, Typography } from '@mui/material'
import { ConfigDiff, PortConfigDiff, PortNumber, isPortConfigDiffTypeBad } from '../types/ports'
import { PageTitle } from './atoms/PageTitle'
import { isDebug } from '../utils/misc'
import { Trans } from 'react-i18next'
import React from 'react'
import { LinkTo } from './atoms/LinkTo'
import { ID } from '../types/models'
import { useNavigate } from 'react-router-dom'
import { SystemControl } from '../classes/SystemControl'

export interface ConfigDiffExplanationProps {
  diff: PortConfigDiff
}

export function ConfigDiffExplanation(props: ConfigDiffExplanationProps): React.ReactElement {

  const { diff } = props
  const { type } = diff
  const sx = { ml: 2 }
  if (type === 'READ FAILED') {
    return <Box sx={sx}><Trans>Configuration reading has failed.</Trans></Box>
  }
  if (type === 'MISSING') {
    return <Box sx={sx}><Trans>Configuration is missing.</Trans></Box>
  }
  if (type === 'DISABLED') {
    return <Box sx={sx}><Trans>Port is disabled.</Trans></Box>
  }
  if (type === 'FUTURE' || type === 'OK') {
    return <></>
  }
  if (type === 'DIFFERS') {
    const variables = Object.keys({ ...diff.config, ...diff.hardware })
    return <Box sx={{ ...sx, display: 'grid', gridTemplateColumns: '1fr 1fr 1fr' }}>
      <Box sx={{ textDecoration: 'underline' }} key=""><Trans>Name</Trans></Box>
      <Box sx={{ textDecoration: 'underline' }}><Trans>Hardware</Trans></Box>
      <Box sx={{ textDecoration: 'underline' }}><Trans>Software</Trans></Box>
      {variables.map(v => <React.Fragment key={v}>
        <Box>{v}</Box>
        <Box>{JSON.stringify(diff.hardware[v])}</Box>
        <Box>{JSON.stringify(diff.config[v])}</Box>
      </React.Fragment>)}
    </Box>
  }
  throw new Error(`A diff type of ${type} not implemented.`)
}

export interface ConfigDiffListProps {
  id: ID
  diff: ConfigDiff
  control: SystemControl
}

export function ConfigDiffList(props: ConfigDiffListProps): React.ReactElement {

  const { diff, id, control } = props
  const bad = Object.keys(diff).filter(k => isPortConfigDiffTypeBad(diff[k as PortNumber]))
  const navigate = useNavigate()

  if (bad.length === 0) {
    return <></>
  }

  const onClick = (id: number, portNumber: PortNumber) => {
    const url = `/config/${id}/${diff[portNumber].id}`
    navigate(url)
    const portCtrl = control.ports({ portId: diff[portNumber].id })[0]
    setTimeout(() => portCtrl.gotoPort(diff[portNumber].id), 200)
  }

  return (
    <Box sx={{ backgroundColor: 'error.light', color: 'white', p: 2 }}>
      <Typography color="white" align="center" fontSize={20} fontWeight={500}><Trans>Some inconsistenies found in the settings</Trans></Typography>
      {bad.map(portNumber => <Box sx={{ mt: 2 }} key={portNumber}>
          <LinkTo title={
            <PageTitle
              title={`${diff[portNumber as PortNumber].name}${isDebug('port-numbers') ? ` [#${diff[portNumber as PortNumber].id} ${portNumber}]` : ''}`}

            />}
            onClick={() => onClick(id, portNumber as PortNumber)}
          />
          <ConfigDiffExplanation diff={diff[portNumber as PortNumber]}/>
        </Box>)
      }
    </Box>
  )
}
